import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { I18nService } from '@gruposaltaedu/liv-i18n/core';
import { MonoTypeOperatorFunction, Observable, pipe, throwError } from 'rxjs';
import { catchError, map, retry } from 'rxjs/operators';
import {
  getRemoteError,
  translateRemoteError
} from 'src/app/core/errors/helpers/get-remote-error';
import { AppErrorCode } from 'src/app/errors/enums/app-error-code.enum';
import { AppErrorNavigationService } from 'src/app/errors/services/app-error-navigation.service';
import { shouldRetry } from 'src/app/shared/rxjs/custom-operators';

import { PortfolioStorage } from '../enums/portfolio-storage.enum';
import {
  LivPortfolioErrorResponse,
  LivPortfolioResponseProtocol,
  LivPortfolioSuccessResponse,
  Meta
} from '../models/liv-portfolio-response-protocol.model';
import { StorageService } from '../services/storage.service';

export const urisToIgnoreOn401ErrorRequest = ['kibana', 'login'];

export function requestIgnore401RequestError(
  request: HttpErrorResponse
): boolean {
  return !new RegExp(urisToIgnoreOn401ErrorRequest.join('|')).test(request.url);
}

@Injectable()
export class ResponseProtocolInterceptor implements HttpInterceptor {
  private readonly storageService = inject(StorageService);
  private readonly i18n = inject(I18nService);
  private readonly appErrorNavigation = inject(AppErrorNavigationService);

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<
    HttpEvent<LivPortfolioSuccessResponse | LivPortfolioErrorResponse>
  > {
    return next.handle(req).pipe(
      map((event: HttpEvent<unknown>) => {
        if (event instanceof HttpResponse) {
          return event.clone({ body: this.handleResponse(event) });
        }
        return event;
      }),
      this.retryUnlessUpload(req),
      catchError((err: HttpErrorResponse) => this.handleError(err))
    );
  }

  /**
   * Uploads are not retried: a slow or failed upload would be sent again in
   * full, multiplying the wait and the load on the API. Chunked uploads
   * (parts and completion) already retry on their own, in ChunkUploader.
   */
  private retryUnlessUpload<T>(
    req: HttpRequest<unknown>
  ): MonoTypeOperatorFunction<T> {
    if (req.body instanceof FormData || /\/chunk(\/complete)?$/.test(req.url)) {
      return pipe();
    }

    return retry(
      shouldRetry({
        maxRetryAttempts: 2,
        scalingDuration: 3000,
        excludedStatusCodes: [404, 403, 401, 400, 429]
      })
    );
  }

  handleResponse(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    event: HttpResponse<any>
  ): LivPortfolioSuccessResponse | LivPortfolioErrorResponse {
    const response: LivPortfolioResponseProtocol = event.body;

    if (
      event.url?.includes('assets') ||
      event.url?.includes('google') ||
      event.body instanceof Blob
    ) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return event.body;
    }

    if (response.data) {
      return {
        data: response.data,
        meta: response.meta || ({} as Meta)
      };
    } else {
      if (response.error) {
        if (response.error.status === 401) {
          throw new HttpErrorResponse({
            status: response.error.status,
            error: response.error,
            url: event.url
          });
        }
      }
      throw new HttpErrorResponse({
        status: event.status,
        error: event.body,
        url: event.url
      });
    }
  }

  handleError(response: HttpErrorResponse): Observable<never> {
    if (response.status === 401 && requestIgnore401RequestError(response)) {
      this.removeAccessTokens();
      void this.appErrorNavigation.navigate(AppErrorCode.Session);
      // throwError (não EMPTY): evita completar o stream sem emissão e
      // deixar o guard/bootstrap pendurados.
      return throwError(() => {
        return {
          status: response.status,
          error: {
            message:
              this.i18n.instant('messages.sessionExpired') || 'Session expired',
            details: {
              url: response.url
            }
          }
        } as LivPortfolioErrorResponse;
      });
    }

    return throwError(() => this.createErrorResponse(response));
  }

  /**
   * Monta o envelope com a mensagem já traduzida. O toast genérico fica no
   * `GlobalErrorHandlerService`, só para erros que ninguém tratou.
   */
  private createErrorResponse(
    response: HttpErrorResponse
  ): LivPortfolioErrorResponse {
    const remote = translateRemoteError(
      getRemoteError(response, {
        translate: (key) => this.i18n.instant(key) as string
      }),
      this.i18n
    );
    const apiError = (response.error as LivPortfolioErrorResponse)?.error;

    return {
      status: remote.status || response.status,
      error: {
        message: remote.message,
        severity: remote.severity,
        code: remote.code,
        details: {
          ...apiError?.details,
          url: response.url
        }
      }
    } as LivPortfolioErrorResponse;
  }

  private removeAccessTokens() {
    this.storageService.delete(PortfolioStorage.AccessToken);
    this.storageService.delete(PortfolioStorage.PortalToken);
  }
}
